import {
    MAX_PLOT_BREITEN, gleicheSigePlanVorlagen, gueltigePlotBreite, normalisiereSigePlanVorlagen, type SigePlanVorlagen,
} from '../../services/sigePlanVorlagen';

/**
 * Bearbeitungsstand des Abschnitts „SiGe-Plan: Plotter-Vorlagen" in den
 * globalen Einstellungen.
 *
 * Die Eingabefelder brauchen einen eigenen Entwurf neben dem gespeicherten
 * Block: wer „914" tippt, hat zwischendurch „9" und „91" im Feld. Ginge jeder
 * Tastendruck direkt durch die Normalisierung, verschwände die Zeile beim
 * ersten Zeichen (außerhalb 300–1500 mm). Der Entwurf hält deshalb die rohen
 * Eingaben, markiert ungültige und doppelte Zeilen, und nach außen geht er
 * erst, wenn er vollständig gültig ist (`vorlagenAusEntwurf`).
 *
 * Die Standardbreite hängt an der ZEILE (`standardId`), nicht an der Zahl:
 * ändert der Admin die Breite der Standardzeile von 610 auf 600, bleibt sie
 * der Standard.
 */

export interface EntwurfZeile {
    id: number;
    /** Roh, wie getippt. */
    mm: string;
    bezeichnung: string;
}

export interface VorlagenEntwurf {
    zeilen: EntwurfZeile[];
    standardId: number | null;
    schriftPt: number;
}

export type ZeilenFehler = 'bereich' | 'doppelt' | null;

/** Entwurf aus gespeicherten Vorlagen. Eine Standardbreite außerhalb der Liste wird als Zeile vorne angezeigt. */
export const entwurfAus = (v: SigePlanVorlagen): VorlagenEntwurf => {
    const zeilen: EntwurfZeile[] = v.breiten.map((b, i) => ({ id: i + 1, mm: String(b.mm), bezeichnung: b.bezeichnung ?? '' }));
    let standard = zeilen.find((z) => gueltigePlotBreite(z.mm) === v.standardBreite);
    if (!standard) {
        standard = { id: zeilen.length + 1, mm: String(v.standardBreite), bezeichnung: '' };
        zeilen.unshift(standard);
    }
    return { zeilen, standardId: standard.id, schriftPt: v.standardSchriftPt };
};

/** Je Zeile: außerhalb des Bereichs / keine Zahl, oder Breite schon weiter oben vorhanden. */
export const zeilenFehler = (zeilen: readonly EntwurfZeile[]): ZeilenFehler[] => {
    const gesehen = new Set<number>();
    return zeilen.map((z) => {
        const mm = gueltigePlotBreite(z.mm);
        if (mm === null) return 'bereich';
        if (gesehen.has(mm)) return 'doppelt';
        gesehen.add(mm);
        return null;
    });
};

/** Mindestens eine Zeile leer, außerhalb des Bereichs oder doppelt? */
export const entwurfUnvollstaendig = (e: VorlagenEntwurf): boolean => zeilenFehler(e.zeilen).some((f) => f !== null);

/**
 * Der Entwurf als Vorlagen — aber NUR, wenn er vollständig gültig ist, sonst
 * null. Solange auch nur eine Zeile unfertig ist, geht nichts hinaus (weder
 * Store noch Autosave), und der zuletzt gültige Block bleibt wirksam.
 *
 * Früher ging der gültige Teil hinaus: wer die Standardbreite leerte, um sie
 * zu überschreiben, verlor in der Tipp-Pause (Autosave nach 2 s) oder beim
 * Schließen die Zeile samt Standard — die erste übrige Zeile war dann
 * gespeicherter Standard (Codex-Review).
 */
export const vorlagenAusEntwurf = (e: VorlagenEntwurf): SigePlanVorlagen | null => {
    if (!e.zeilen.length || entwurfUnvollstaendig(e)) return null;
    const standard = e.zeilen.find((z) => z.id === e.standardId) ?? e.zeilen[0];
    return normalisiereSigePlanVorlagen({
        breiten: e.zeilen.map((z) => ({ mm: gueltigePlotBreite(z.mm), bezeichnung: z.bezeichnung })),
        standardBreite: gueltigePlotBreite(standard.mm),
        standardSchriftPt: e.schriftPt,
    });
};

// ─── Editorzustand: Entwurf gegen nachgeladene Firmenvorlagen ───────────────

/**
 * Der Abschnitt bekommt seinen Stand (`wert`) vom Einstellungsdialog: eigener
 * Block, sonst der firmenweit wirksame aus dem Store. Der kann sich ändern,
 * während der Abschnitt offen ist — der Firmenblock kommt nach dem Login erst
 * an, oder nach „Zurücksetzen" gilt der Block eines anderen Admins. Ohne
 * Abgleich zeigte der Entwurf den alten Stand, und die nächste Änderung
 * speicherte die veraltete Liste als eigenen Block (Codex-Review).
 *
 *  - `basis`           der Stand, auf dem der Entwurf aufsetzt
 *  - `fremdeAenderung` ein neuer Stand von außen, während der Entwurf lokal
 *                      geändert ist — die Oberfläche bietet „Übernehmen" an
 */
export interface EditorZustand {
    basis: SigePlanVorlagen;
    entwurf: VorlagenEntwurf;
    fremdeAenderung: SigePlanVorlagen | null;
}

export const editorStart = (wert: SigePlanVorlagen): EditorZustand => ({
    basis: wert,
    entwurf: entwurfAus(wert),
    fremdeAenderung: null,
});

/**
 * Die Basis so, wie der Editor sie ausgäbe: einmal durch Entwurf und zurück.
 * Nicht dasselbe wie die Basis selbst — steht deren Standardbreite nicht in
 * der Liste, zeigt `entwurfAus` sie als Zeile, und die Ausgabe trägt sie dann
 * in `breiten`.
 */
const basisImRundlauf = (basis: SigePlanVorlagen): SigePlanVorlagen => vorlagenAusEntwurf(entwurfAus(basis)) ?? basis;

/**
 * Hat der Admin nichts geändert? Verglichen wird gegen die Basis im selben
 * Rundlauf (Codex-Review): sonst galt ein unberührter Entwurf als geändert,
 * sobald die Standardbreite nicht in der Liste stand — ein nachgeladener
 * Firmenblock erschien dann nur als Konflikt.
 */
export const entwurfUnveraendert = (z: EditorZustand): boolean => {
    const v = vorlagenAusEntwurf(z.entwurf);
    return v !== null && gleicheSigePlanVorlagen(v, basisImRundlauf(z.basis));
};

/**
 * Neuer `wert` von außen:
 *  - gleicher Inhalt wie die Basis → nichts
 *  - genau das, was der Entwurf gerade ausgegeben hat (Echo der eigenen
 *    Änderung über Autosave/Store) → nur die Basis nachziehen
 *  - Entwurf unverändert → neuen Stand übernehmen
 *  - Entwurf lokal geändert → nichts überschreiben, `fremdeAenderung` merken
 */
export const wertAktualisieren = (z: EditorZustand, wert: SigePlanVorlagen): EditorZustand => {
    if (gleicheSigePlanVorlagen(wert, z.basis)) return z.fremdeAenderung ? { ...z, fremdeAenderung: null } : z;
    const ausgabe = vorlagenAusEntwurf(z.entwurf);
    if (ausgabe && gleicheSigePlanVorlagen(ausgabe, wert)) return { ...z, basis: wert, fremdeAenderung: null };
    if (entwurfUnveraendert(z)) return editorStart(wert);
    return { ...z, fremdeAenderung: wert };
};

/** „Übernehmen": lokale Änderung verwerfen, den neuen Stand von außen zeigen. */
export const fremdeAenderungUebernehmen = (z: EditorZustand): EditorZustand =>
    editorStart(z.fremdeAenderung ?? z.basis);

const naechsteId = (zeilen: readonly EntwurfZeile[]): number => zeilen.reduce((max, z) => Math.max(max, z.id), 0) + 1;

/** Neue leere Zeile am Ende (höchstens MAX_PLOT_BREITEN). */
export const zeileHinzufuegen = (e: VorlagenEntwurf): VorlagenEntwurf =>
    e.zeilen.length >= MAX_PLOT_BREITEN
        ? e
        : { ...e, zeilen: [...e.zeilen, { id: naechsteId(e.zeilen), mm: '', bezeichnung: '' }] };

/**
 * Zeile entfernen. Die letzte bleibt stehen (eine leere Auswahl gibt es
 * nicht); war sie der Standard, wird die erste verbleibende gültige Zeile
 * zum Standard.
 */
export const zeileEntfernen = (e: VorlagenEntwurf, id: number): VorlagenEntwurf => {
    if (e.zeilen.length <= 1) return e;
    const zeilen = e.zeilen.filter((z) => z.id !== id);
    if (e.standardId !== id) return { ...e, zeilen };
    const fehler = zeilenFehler(zeilen);
    const neu = zeilen.find((_, i) => fehler[i] === null) ?? zeilen[0];
    return { ...e, zeilen, standardId: neu?.id ?? null };
};

/** Zeile um eine Position nach oben (-1) oder unten (+1); am Rand unverändert. */
export const zeileVerschieben = (e: VorlagenEntwurf, id: number, richtung: -1 | 1): VorlagenEntwurf => {
    const i = e.zeilen.findIndex((z) => z.id === id);
    const j = i + richtung;
    if (i < 0 || j < 0 || j >= e.zeilen.length) return e;
    const zeilen = [...e.zeilen];
    [zeilen[i], zeilen[j]] = [zeilen[j], zeilen[i]];
    return { ...e, zeilen };
};

export const zeileAendern = (e: VorlagenEntwurf, id: number, teil: Partial<Omit<EntwurfZeile, 'id'>>): VorlagenEntwurf => ({
    ...e,
    zeilen: e.zeilen.map((z) => (z.id === id ? { ...z, ...teil } : z)),
});
